'use strict';
import { cn_object_instance } from '../model/cn_object_instance';
import { cn_roof } from '../model/cn_roof';
import { cn_space } from '../model/cn_space';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_edit_box } from './cn_edit_box';
import {
    cn_description_input,
    cn_input_generic_field_boolean,
    cn_input_generic_field_number,
    cn_input_generic_field_string,
    cn_label_input,
    cn_number_input,
    cn_number_list_input,
    cn_space_equipments_input,
    cn_space_input,
    cn_space_numerotation_input,
} from './cn_inputs';
import { cn_pastille } from './cn_pastille';
import { cn_svg_map} from './cn_svg_map';
import { extension_instance } from '../extension/cn_extension';
import { logger } from '../utils/cn_logger';
import { cn_event_handler } from './cn_event_handler';
import { cn_mouse_event } from './cn_mouse_event';

export class cn_space_handler extends cn_edition_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {Array<cn_space>} spaces
     * @param {cn_svg_map} map
     */
    constructor(spaces, map) {
        super(spaces, map);

        this._map = map;
        this._scene = spaces[0].scene;
        this._transaction_manager =
            spaces[0].scene.storey.building.transaction_manager;
        this._roof = this._scene.constructor == cn_roof;
        this._storey = spaces[0].scene.storey;
        this._building = this._storey.building;
        //*** marker
        this._spaces = spaces;

        if (spaces.length == 1 ) {
            this.numerotation_handler = new cn_numerotation_handler(spaces[0],this);
            this._handlers.push(this.numerotation_handler);
        }

        //*** Edit box for mass selection */
        const edit_box = new cn_edit_box(this, spaces, this._readOnly, false);
        this._handlers.push(edit_box);

        const space_extension = extension_instance.space;

        const obj = this;

        const inside_spaces = spaces.filter((sp) => !sp.outside);
        const ceiling_spaces = inside_spaces.filter(
            (sp) => sp.has_roof && sp.has_ceiling
        );

        //*** details */
        const same_space_type = inside_spaces.every((sp) => sp.space_type.ID === inside_spaces[0].space_type.ID);
        if (inside_spaces.length && same_space_type) {
            this._details_pastille = new cn_pastille([0, 0], 'text.svg');
            edit_box.add_pastille(this._details_pastille);
            this._details_pastille.svg_class = 'pastille_background white';
            this._details_pastille.title = 'Détails';
            const storey_ID = this._storey.ID;
            this._details_pastille.clicked = () => {
                const input = new cn_space_input();
                input.ID = inside_spaces.some(
                    (sp) => sp.ID !== inside_spaces[0].ID
                )
                    ? undefined
                    : inside_spaces[0].ID;
                input.storey_ID = storey_ID;
                input.readonly = this._readOnly;
                input.space_type = inside_spaces[0].space_type;

                //*** Name and usage */
                input.name = new cn_input_generic_field_string(
                    inside_spaces.map((sp) => sp.name)
                );

                //*** ceiling */
                const roof_spaces = spaces.filter((sp) => sp.has_roof);
                const has_ceiling_relevant =
                    roof_spaces.length === inside_spaces.length;
                input.has_ceiling = new cn_input_generic_field_boolean(
                    roof_spaces.map((sp) => sp.has_ceiling),
                    has_ceiling_relevant
                );

                //*** label visibility */
                input.space_label_visibility =
                    new cn_input_generic_field_number(
                        inside_spaces.map((sp) => sp.space_label_visibility)
                    );

                //*** space parenthood */
                let candidate_spaces = inside_spaces[0].get_parent_candidates();
                inside_spaces
                    .filter((sp, index) => index > 0)
                    .forEach((sp) => {
                        const parent_candidates = sp.get_parent_candidates();
                        candidate_spaces = candidate_spaces.filter((sp) =>
                            parent_candidates.includes(sp)
                        );
                    });
                input.candidate_parent_spaces = candidate_spaces.map((sp) =>
                    sp.get_name(this._storey)
                );
                input.parent_space = new cn_input_generic_field_number(
                    inside_spaces.map((sp) =>
                        candidate_spaces.indexOf(sp.parent_space)
                    )
                );

                input.callback = () => {
                    obj._transaction_manager.push_transaction(
                        'Détails de pièces'
                    );
                    inside_spaces.forEach((sp) => {
                        obj._transaction_manager.push_item_set(sp, ['name']);
                        if (input.name.value !== undefined)
                            sp.name = input.name.value;
                    });
                    if (
                        input.has_ceiling.is_relevant &&
                        input.has_ceiling.value !== undefined
                    ) {
                        roof_spaces.forEach((sp) => {
                            obj._transaction_manager.push_item_set(sp, [
                                'has_ceiling',
                            ]);
                            sp.has_ceiling = input.has_ceiling.value;
                        });
                    }
                    if (input.space_label_visibility.value !== undefined) {
                        inside_spaces.forEach((sp) => {
                            obj._transaction_manager.push_item_set(sp, [
                                'space_label_visibility',
                            ]);
                            sp.space_label_visibility =
                                input.space_label_visibility.value;
                        });
                    }
                    if (input.parent_space.value !== undefined) {
                        const parent_space =
                            candidate_spaces[input.parent_space.value];
                        inside_spaces.forEach((sp) =>
                            sp.set_parent_space(
                                parent_space,
                                obj._transaction_manager
                            )
                        );
                    }
                    if (spaces.length) {
                        spaces[0].scene.building.update_roofs();
                    }

                    map.refresh();
                };
                map.call('space_input', input);
            };
        }

        if (!this._storey.exterior && !this._readOnly) {
            //*** Floor */
            this._floor_pastille = new cn_pastille(
                [0, 0],
                'arrow_collapse_down.svg'
            );
            edit_box.add_pastille(this._floor_pastille);
            this._floor_pastille.svg_class = 'pastille_background white';
            this._floor_pastille.title = 'Décalage de dalle';
            this._floor_pastille.clicked = () => {
                const initial_value = spaces[0].slab_offset;
                const initial_value_cm = initial_value * 100;
                const input = new cn_number_input(
                    'Décalage de dalle',
                    initial_value_cm,
                    'cm',
                    0,
                    -500,
                    500
                );
                if (spaces.some((s) => s.slab_offset !== initial_value))
                    input.label += ' (variable)';
                input.callback = () => {
                    const result_meters = input.value / 100;
                    obj._transaction_manager.push_transaction(
                        'Décalage de dalle'
                    );
                    spaces.forEach((sp) => {
                        obj._transaction_manager.push_item_set(sp, [
                            'slab_offset',
                        ]);
                        sp.slab_offset = result_meters;
                    });
                    map.refresh();
                };
                map.call('number_input', input);
            };
        }

        //*** Equipments */
        if (
            !this._storey.exterior &&
            this._spaces.length === 1 &&
            !this._spaces[0].outside &&
            !this._readOnly
        ) {
            const space = this._spaces[0];
            const equipment_pastille = new cn_pastille([0, 0], 'shape.svg');
            edit_box.add_pastille(equipment_pastille);
            equipment_pastille.svg_class = 'pastille_background white';
            equipment_pastille.title = 'Equipements';
            equipment_pastille.clicked = () => {
                const input = new cn_space_equipments_input(
                    space.get_name(this._storey),
                    space.get_space_usage()
                );
                const instances = {};
                space.scene.object_instances.forEach((instance) => {
                    if (instance.space === space) {
                        const oid = instance.object.ID;
                        if (typeof input.objects[oid] == 'undefined') {
                            input.objects[oid] = 1;
                            instances[oid] = [instance];
                        } else {
                            input.objects[oid]++;
                            instances[oid].push(instance);
                        }
                    }
                });
                //*** Callback definition */
                input.callback = () => {
                    let first_modification = true;

                    //*** Unreturned objects are considered as zero instance */
                    const input_objects_ids = Object.keys(input.objects);
                    const removed_objects_ids = Object.keys(instances).filter(
                        (it) => !input_objects_ids.includes(it)
                    );
                    const result_objects = {
                        ...input.objects,
                        ...Object.fromEntries(
                            removed_objects_ids.map((id) => [id, 0])
                        ),
                    };

                    for (let object_id in result_objects) {
                        const number = result_objects[object_id];
                        //*** For each object, we check existing instances of that object */
                        const existing_instances =
                            typeof instances[object_id] == 'undefined'
                                ? []
                                : instances[object_id];

                        //*** Create transaction if necessary */
                        if (first_modification) {
                            first_modification = false;
                            obj._transaction_manager.push_transaction(
                                'Equipements de pièce'
                            );
                            obj._transaction_manager.push_item_set(
                                space.scene,
                                ['object_instances']
                            );
                        }

                        //*** Maybe we have to add some instances */
                        if (number > existing_instances.length) {
                            if (number === existing_instances.length) return;
                            const object =
                                space.scene.building.get_object(object_id);
                            if (object) {
                                for (
                                    let k = 0;
                                    k < number - existing_instances.length;
                                    k++
                                ) {
                                    const new_object_instance =
                                        new cn_object_instance(space.scene);
                                    new_object_instance.virtual = true;
                                    new_object_instance.space = space;
                                    new_object_instance.object = object;
                                    space.scene.object_instances.push(
                                        new_object_instance
                                    );
                                }
                            }
                        }

                        //*** Maybe we have to remove some */
                        else if (number < existing_instances.length) {
                            //*** we remove in priority virtual instances */
                            existing_instances.sort(function (a, b) {
                                const xa = a.virtual ? 0 : 1;
                                const xb = b.virtual ? 0 : 1;
                                return xa - xb;
                            });
                            logger.log(
                                'existing instances',
                                existing_instances
                            );
                            for (
                                let k = 0;
                                k < existing_instances.length - number;
                                k++
                            ) {
                                const index =
                                    space.scene.object_instances.indexOf(
                                        existing_instances[k]
                                    );
                                if (index >= 0)
                                    space.scene.object_instances.splice(
                                        index,
                                        1
                                    );
                            }
                        }
                    }

                    space.scene.full_update();
                    map.refresh();
                };

                map.call('space_equipments_input', input);
            };
        }

        //*** dimensions */
        if (this._spaces.length == 1) {
            const dimension_title = 'Dimensions déclarées';
            this._dimension_pastille = new cn_pastille(
                [0, 0],
                'tape_measure.svg'
            );
            edit_box.add_pastille(this._dimension_pastille);
            this._dimension_pastille.svg_class = 'pastille_background white';
            this._dimension_pastille.title = dimension_title;
            this._dimension_pastille.clicked = () => {
                const input = new cn_number_list_input(dimension_title);
                input.values.push(
                    new cn_number_input(
                        'Surface',
                        spaces[0].declared_area > 0
                            ? spaces[0].declared_area
                            : spaces[0].area,
                        'm²',
                        2,
                        0,
                        10000,
                        'Automatique',
                        spaces[0].declared_area <= 0
                    )
                );
                input.values.push(
                    new cn_number_input(
                        'Périmètre',
                        spaces[0].declared_perimeter > 0
                            ? spaces[0].declared_perimeter
                            : spaces[0].perimeter,
                        'm',
                        2,
                        0,
                        10000,
                        'Automatique',
                        spaces[0].declared_perimeter <= 0
                    )
                );
                this._building.area_contexts.forEach(area_context => {
                    let nb_sub_contexts = area_context.sub_labels.length || 1;
                    for (var sub_context=0;sub_context < nb_sub_contexts; sub_context++) {
                        let v = spaces[0].get_area_from_context(area_context,sub_context,this._storey,false);
                        let declared = spaces[0].is_declared_area_from_context(area_context,sub_context);
                        let checkbox_label = 'Automatique';
                        if (sub_context >= area_context.active_sub_labels) {
                            checkbox_label = '';
                            declared = true;
                        }
                        input.values.push(
                            new cn_number_input(
                                (nb_sub_contexts == 1)?area_context.label:area_context.sub_labels[sub_context],
                                v,
                                'm',
                                2,
                                0,
                                10000,
                                checkbox_label,
                                !declared
                            )
                        );
                    }
                });

                input.callback = () => {
                    const new_area = input.values[0].checkbox_status
                        ? 0
                        : input.values[0].value;
                    const new_perimeter = input.values[1].checkbox_status
                        ? 0
                        : input.values[1].value;
                    obj._transaction_manager.push_transaction('Dimensions');
                    spaces.forEach((sp) => {
                        obj._transaction_manager.push_item_set(sp, [
                            'declared_area',
                            'declared_perimeter',
                            'declared_area_context'
                        ]);
                        sp.declared_area = new_area;
                        sp.declared_perimeter = new_perimeter;
                        let idx=2;
                        this._building.area_contexts.forEach(area_context => {
                            let nb_sub_contexts = area_context.sub_labels.length || 1;
                            for (var sub_context=0;sub_context < nb_sub_contexts; sub_context++) {
                                let new_value = input.values[idx].checkbox_status?-1:input.values[idx].value;
                                idx++;
                                sp.set_declared_area_from_context(new_value,area_context,sub_context);
                            }
                        });
                    });
                };
                map.call('number_list_input', input);
            };
        }

        //*** Diagonals */
        if (
            this._spaces.length == 1 &&
            this._map.show_space_measure &&
            !this._readOnly
        ) {
            var diagonals_mode = false;
            const diagonals_pastille = new cn_pastille(
                [0, 0],
                'set_square.svg'
            );
            edit_box.add_pastille(diagonals_pastille);
            diagonals_pastille.svg_class = 'pastille_background white';
            diagonals_pastille.title = 'Diagonales';
            diagonals_pastille.clicked = () => {
                diagonals_mode = !diagonals_mode;
                if (diagonals_mode)
                    this._spaces[0].measure.update();
                obj.call('diagonals_mode', diagonals_mode);
                if (diagonals_mode)
                    diagonals_pastille.svg_class = 'pastille_background green';
                else diagonals_pastille.svg_class = 'pastille_background white';
            };
        }

        if (this._spaces.length === 1 && this._spaces[0].space_type) {
            const pastille = new cn_pastille([0, 0], 'numeric.svg');
            pastille.svg_class = 'pastille_background white';
            pastille.title = 'Numérotation';
            let space = this._spaces[0];
            pastille.clicked = () => {
                const input_ext = new cn_space_numerotation_input();
                input_ext.name = space.get_name_from_space_type();
                input_ext.numerotation = space.space_type.numerotation;
                input_ext.prefix = space.space_type.prefix;
                input_ext.value = space.custom_index;
                input_ext.callback = () => {
                    const new_numerotation = input_ext.numerotation;
                    const new_prefix = input_ext.prefix;
                    const new_value = input_ext.value;
                    obj._transaction_manager.push_transaction('Valeur de numérotation');
                    obj._transaction_manager.push_item_set(space.space_type, ['numerotation', 'prefix']);
                    obj._transaction_manager.push_item_set(space, 'custom_index');
                    space.space_type.numerotation = new_numerotation;
                    space.space_type.prefix = new_prefix;
                    space.custom_index = new_value;
                    map.refresh();
                };
                map.call('space_numerotation_input', input_ext);
            };
            edit_box.add_pastille(pastille);
        }
        
        /*if (this._spaces.length === 1 && this._storey.get_unique_space_labels().length) {
            const pastille = new cn_pastille([0, 0], 'label-outline.svg');
            if (this._spaces[0].unique_label[this._storey.ID])
                pastille.svg_class = 'pastille_background green';
            else
                pastille.svg_class = 'pastille_background orange';
            pastille.title = 'Etiquette';
            pastille.clicked = () => {
                let input = new cn_label_input("Choisir l'étiquette de pièce",0,[]);
                let labels = this._storey.get_unique_space_labels();
                input.choices = [{label:"Aucune",used:false}, ...labels.map(stl => {return {label:stl.name,used:stl.element != null};})];
                input.selection = 1 + labels.indexOf(this._spaces[0].unique_label[this._storey.ID]);
                input.callback = () => {
                    this._building.set_space_unique_label(this._spaces[0],this._storey,input.selection>0?labels[input.selection-1]:null);
                    if (this._spaces[0].unique_label[this._storey.ID])
                        pastille.svg_class = 'pastille_background green';
                    else
                        pastille.svg_class = 'pastille_background orange';
                    map.refresh();
                };
                map.call('label_input', input);
            };
            edit_box.add_pastille(pastille);
        }*/

    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera) {
        let html = "";
        html += super.draw(camera);
        return html;
    }
}

class cn_numerotation_handler extends cn_event_handler {
    constructor(space, parent) {
        super(parent);
        this.space = space;
        this._mouseover_numerotation = null;
    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera) {
        let html = "";
        this.space._numerotation.forEach(num => {
            html += num.draw_pastille(camera,num == this._mouseover_numerotation);
        });
        return html;
    }

    /**
     * 
     * @param {cn_mouse_event} event 
     * @returns {boolean}
     */
    move(event) {
        this._mouseover_numerotation = this.space._numerotation.find(num => num.under_point(event.mouse_screen))
        return !!this._mouseover_numerotation;
    }

    /**
     * 
     * @param {cn_mouse_event} event 
     * @returns {boolean}
     */
    click(event) {
        if (this._mouseover_numerotation) {
            let transaction_manager = this.space.scene.storey.building.transaction_manager;
            transaction_manager.push_transaction("Numérotation d'espace");
            transaction_manager.push_item_set(this.space, 'numerotation_space_segment');
            this.space.numerotation_space_segment = this._mouseover_numerotation.space_segment.ID;
            this.space.update_numerotation();
            return true;
        }
        return false;
    }
}